import { ShieldCheck } from 'lucide-react';
import type { User } from '@/shared/types';
import { Avatar } from '@/shared/ui/Avatar';
import { useStore } from '@/shared/state/store';
import { useAuth } from '@/shared/state/auth-context';
import { formatCount } from '@/shared/utils/format';
import { cn } from '@/shared/utils/cn';

export function ProfileHeader({ user }: { user: User }) {
  const { currentUser } = useAuth();
  const isMe = currentUser?.id === user.id;
  const followed = useStore((s) => s.followedUserIds.has(user.id));
  const toggleFollow = useStore((s) => s.toggleFollow);

  return (
    <section data-testid="profile-header" data-username={user.username} className="px-4 pt-4">
      <div className="flex items-center gap-5">
        <Avatar
          src={user.avatar}
          alt={user.username}
          size={88}
          ring={user.verified ? 'rose' : 'none'}
        />
        <div className="flex-1 grid grid-cols-3 text-center">
          <Stat n={user.postsCount} label="Posts" />
          <Stat n={user.followersCount + (followed ? 1 : 0)} label="Followers" />
          <Stat n={user.followingCount} label="Following" />
        </div>
      </div>
      <div className="mt-4">
        <div className="flex items-center gap-1.5">
          <span className="text-white font-medium">{user.displayName}</span>
          {user.verified && (
            <ShieldCheck className="w-3.5 h-3.5 text-rose-300" />
          )}
        </div>
        <div className="text-white/60 text-sm mt-0.5">@{user.username}</div>
        <p className="text-white/70 text-sm mt-2 leading-relaxed">{user.bio}</p>
      </div>
      <div className="mt-4">
        {isMe ? (
          <button className="w-full glass rounded-xl py-2 text-sm font-medium active:scale-[0.98]">
            Edit profile
          </button>
        ) : (
          <button
            data-testid="profile-follow"
            data-followed={followed}
            onClick={() => toggleFollow(user.id)}
            className={cn(
              'w-full rounded-xl py-2 text-sm font-medium active:scale-[0.98] transition-all',
              followed ? 'glass' : 'bg-rose-100 text-velvet shadow-rose',
            )}
          >
            {followed ? 'Following' : 'Follow'}
          </button>
        )}
      </div>
    </section>
  );
}

function Stat({ n, label }: { n: number; label: string }) {
  return (
    <div>
      <div className="text-white font-semibold">{formatCount(n)}</div>
      <div className="text-[11px] uppercase tracking-wider text-white/50">
        {label}
      </div>
    </div>
  );
}
